/**
 * The controls `show_in_viewer` can point at — ONLY those with a stable
 * identifier (`data-testid`) already in the viewer, so nothing in the
 * viewer had to change. Each entry: the selector, the control's name as
 * the viewer shows it, how to use it, what makes it appear (when it can be
 * missing), and — for an item inside a closed menu — the target whose
 * button opens that menu. `sourceCheck` lists literal fragments that must
 * exist in the viewer source (unit-tested), so a renamed identifier fails
 * a test instead of silently pointing at nothing.
 */
import type { ShowInViewerTarget } from '@agent/tool-names';

export interface TargetSpec {
    /** CSS selector; the first visible match is used. */
    readonly selector: string;
    readonly label: string;
    readonly howTo: string;
    /** What makes it appear / enabled. */
    readonly needs: string;
    /** For an item inside a closed menu: the target that opens it. */
    readonly opener?: ShowInViewerTarget;
    /** The workflow step (a step-* target) the control lives on. */
    readonly step?: ShowInViewerTarget;
    /** Literal source fragments proving the identifier exists. */
    readonly sourceCheck: ReadonlyArray<string>;
}

const tid = (id: string) => `[data-testid="${id}"]`;
const tidPrefix = (prefix: string) => `[data-testid^="${prefix}"]`;
const one = (id: string) => ({ selector: tid(id), sourceCheck: [id] });

const SELECTED = 'Select a structure in the structure list (Contouring step).';
const SET_LOADED = 'Open the Contouring step with a structure set loaded.';
const CONTOURING =
    'Open the Contouring or Review step and select a contoured structure ' +
    'that is not approved.';
const step = (id: string, label: string): TargetSpec => ({
    selector: tid(`${id}-nav-button`),
    label: `${label} step`,
    howTo: `Click “${label}” in the workflow steps on the left.`,
    needs:
        `The ${label} step is not offered for this workflow or patient; ` +
        'load a patient first.',
    sourceCheck: ['-nav-button`', `id: '${id}'`],
});

export const SHOW_IN_VIEWER_CATALOGUE: Record<ShowInViewerTarget, TargetSpec> =
    {
        // ---- structure list / selected structure
        'ai-assistant': {
            ...one('ai-assistant-button'),
            label: 'AI Assistant',
            howTo: 'Click “AI Assistant” at the top of the left panel.',
            needs: 'Load a patient and an image first.',
        },
        'toggle-all-visibility': {
            step: 'step-contouring',
            ...one('toggle-all-visibility'),
            label: 'Show/hide all structures',
            howTo: 'Click the eye icon above the structure list.',
            needs: SET_LOADED,
        },
        'new-structure': {
            step: 'step-contouring',
            ...one('create-roi-button'),
            label: '+ New',
            howTo:
                'Click “+ New” above the structure list, then choose ' +
                '“Structure” or “Point” (marker).',
            needs: `${SET_LOADED} It is disabled for an approved structure set.`,
        },
        'focus-structure': {
            step: 'step-contouring',
            selector: tidPrefix('structure-details-focus-button-'),
            sourceCheck: ['structure-details-focus-button-'],
            label: 'Focus',
            howTo:
                'Click the focus icon next to the selected structure’s name ' +
                'to zoom to it.',
            needs: SELECTED,
        },
        'rename-structure': {
            step: 'step-contouring',
            selector: tidPrefix('structure-edit-button-'),
            sourceCheck: ['structure-edit-button-'],
            label: 'Rename',
            howTo:
                'Click the pencil next to the selected structure’s name (or ' +
                'double-click the name), type the new name, press Enter.',
            needs: `${SELECTED} Not available for approved structures.`,
        },
        'structure-properties': {
            step: 'step-contouring',
            selector: tidPrefix('structure-info-button-'),
            sourceCheck: ['structure-info-button-'],
            label: 'Properties (i)',
            howTo:
                'Click the (i) icon of the selected structure to see its ' +
                'properties and statistics.',
            needs: `${SELECTED} Not shown for markers.`,
        },
        'structure-more-actions': {
            step: 'step-contouring',
            ...one('structure-more-menu-button'),
            label: 'More actions (⋮)',
            howTo:
                'Click ⋮ next to the selected structure for Duplicate, ' +
                'Replace another structure and Delete.',
            needs: SELECTED,
        },
        'delete-structure': {
            step: 'step-contouring',
            ...one('delete-structure-button'),
            label: 'Delete structure',
            opener: 'structure-more-actions',
            howTo:
                'Open ⋮ (More actions) of the selected structure, choose ' +
                '“Delete”, then confirm.',
            needs: `${SELECTED} Not available for approved structures.`,
        },
        'structure-type': {
            step: 'step-contouring',
            ...one('structure-type-edit-button'),
            label: 'Type',
            howTo:
                'Click the pencil next to “Type” in the selected structure’s ' +
                'details and choose the new type.',
            needs: `${SELECTED} Not available for approved structures.`,
        },
        // ---- structure set header, settings, menu
        'structure-set-settings': {
            step: 'step-contouring',
            ...one('structureset-properties-button'),
            label: 'Structure set properties (gear)',
            howTo:
                'Click the gear icon in the structure set header for its ' +
                'approval, label and description.',
            needs: SET_LOADED,
        },
        'approval-status': {
            step: 'step-contouring',
            ...one('approval-status-dropdown'),
            label: 'Approval',
            opener: 'structure-set-settings',
            howTo:
                'Open the structure set gear, choose a status under ' +
                '“Approval”, then confirm.',
            needs: SET_LOADED,
        },
        'structure-set-label': {
            step: 'step-contouring',
            ...one('structure-set-label-field'),
            label: 'Label',
            opener: 'structure-set-settings',
            howTo:
                'Open the structure set gear and edit “Label” (press Enter ' +
                'to save).',
            needs: `${SET_LOADED} Not editable when approved.`,
        },
        'structure-set-description': {
            step: 'step-contouring',
            ...one('structure-set-description-field'),
            label: 'Description',
            opener: 'structure-set-settings',
            howTo: 'Open the structure set gear and edit “Description”.',
            needs: `${SET_LOADED} Not editable when approved.`,
        },
        'approval-chip': {
            step: 'step-contouring',
            ...one('structure-set-approval-chip'),
            label: 'Approval status chip',
            howTo:
                'The chip in the structure set header shows the approval ' +
                'status; clicking it approves or unapproves (with a ' +
                'confirmation).',
            needs: SET_LOADED,
        },
        'structure-set-menu': {
            step: 'step-contouring',
            ...one('structureset-menu-button'),
            label: 'Structure set menu (☰)',
            howTo:
                'Click ☰ in the structure set header for create, copy, ' +
                'change, download and delete.',
            needs: SET_LOADED,
        },
        'create-structure-set': {
            step: 'step-contouring',
            ...one('new-structureset-button'),
            label: 'Create structure set',
            opener: 'structure-set-menu',
            howTo: 'Open ☰ in the structure set header, choose “Create structure set”.',
            needs: SET_LOADED,
        },
        'copy-structure-set': {
            step: 'step-contouring',
            ...one('copy-structure-set-menu-item'),
            label: 'Copy to…',
            opener: 'structure-set-menu',
            howTo: 'Open ☰ in the structure set header, choose “Copy to…”.',
            needs: SET_LOADED,
        },
        'change-structure-set': {
            step: 'step-contouring',
            ...one('change-structureset-button'),
            label: 'Change structure set',
            opener: 'structure-set-menu',
            howTo:
                'Open ☰ in the structure set header, choose “Change ' +
                'structure set”.',
            needs: `${SET_LOADED} Shown only when there is more than one set.`,
        },
        'delete-structure-set': {
            step: 'step-contouring',
            ...one('delete-structureset-button'),
            label: 'Delete structure set',
            opener: 'structure-set-menu',
            howTo:
                'Open ☰ in the structure set header, choose “Delete structure ' +
                'set”, then confirm.',
            needs: SET_LOADED,
        },
        'generate-structure-set': {
            step: 'step-contouring',
            ...one('generate-structureset-button'),
            label: 'Generate structure set (Contour+)',
            opener: 'structure-set-menu',
            howTo:
                'Open ☰ in the structure set header, choose “Generate ' +
                'structure set”.',
            needs: `${SET_LOADED} Shown only when Contour+ is enabled.`,
        },
        // ---- images
        'image-menu': {
            step: 'step-contouring',
            ...one('image-menu-button'),
            label: 'Image options (☰)',
            howTo:
                'Click ☰ in the image header (above the structure set) for ' +
                'blend, change, download and unload.',
            needs: 'Load an image (Contouring step).',
        },
        'change-image': {
            step: 'step-contouring',
            ...one('change-image-button'),
            label: 'Change image',
            opener: 'image-menu',
            howTo: 'Open the image options (☰) and choose “Change image”.',
            needs: 'Shown only when more than one image is available.',
        },
        'unload-image': {
            step: 'step-contouring',
            ...one('unload-image-button'),
            label: 'Unload image',
            opener: 'image-menu',
            howTo:
                'Open the image options (☰) and choose “Unload”. This only ' +
                'removes it from the view.',
            needs: 'Load an image (Contouring step).',
        },
        // ---- contouring toolbar
        'static-brush': {
            step: 'step-contouring',
            ...one('tool-static-brush'),
            label: 'Static Brush',
            howTo: 'Click the static brush in the toolbar above the image.',
            needs: CONTOURING,
        },
        'adaptive-brush': {
            step: 'step-contouring',
            ...one('tool-adaptive-brush'),
            label: 'Adaptive Brush',
            howTo: 'Click the adaptive brush in the toolbar above the image.',
            needs: CONTOURING,
        },
        'line-draw': {
            step: 'step-contouring',
            ...one('tool-line-draw'),
            label: 'Line Draw',
            howTo: 'Click the line draw tool in the toolbar above the image.',
            needs: CONTOURING,
        },
        eraser: {
            step: 'step-contouring',
            ...one('tool-erase'),
            label: 'Erase',
            howTo: 'Click the eraser in the toolbar above the image.',
            needs: CONTOURING,
        },
        transform: {
            step: 'step-contouring',
            ...one('tool-transform'),
            label: 'Transform (move / scale)',
            howTo:
                'Click the transform tool, then drag the structure to move it ' +
                'or its edge to scale it.',
            needs: CONTOURING,
        },
        'line-deform': {
            step: 'step-contouring',
            ...one('tool-line-deform'),
            label: 'Line Deform',
            howTo: 'Click the line deform tool in the toolbar above the image.',
            needs: CONTOURING,
        },
        'full-screen': {
            ...one('full-screen-toggle'),
            label: 'Full screen',
            howTo: 'Click the full-screen icon at the top right of the main view.',
            needs: 'Load an image.',
        },
        // ---- viewport overlay
        'window-level-presets': {
            selector:
                `${tid('windowing-controls-primary')} ` +
                tid('windowing-preset-selector'),
            sourceCheck: ['windowing-controls-', 'windowing-preset-selector'],
            label: 'Window/level presets',
            howTo:
                'Click the preset name at the bottom left of the main view and ' +
                'pick a preset (Lung, Bone, Soft Tissue…).',
            needs: 'Load a CT or MR image (not shown for PET).',
        },
        'fusion-colors': {
            ...one('fusion-color-selector'),
            label: 'Fusion colours',
            howTo:
                'Click the colour selector above the blend slider in the main ' +
                'view.',
            needs: 'Blend a second, non-PET image first.',
        },
        // ---- workflow steps
        'step-setup': step('setup', 'Setup'),
        'step-synthetic-imaging': step(
            'synthetic-imaging',
            'Synthetic imaging'
        ),
        'step-registration': step('registration', 'Registration'),
        'step-contouring': step('contouring', 'Contouring'),
        'step-review': step('review', 'Review'),
        'step-dose': step('predictive-planning', 'Dose'),
        'step-export': step('export', 'Export'),
        // ---- export, settings, data, review, dose, Contour+
        export: {
            step: 'step-export',
            ...one('exportButtonModal'),
            label: 'Export',
            howTo:
                'On the Export step click the export button, choose the items ' +
                'and the target, then confirm.',
            needs: 'Open the Export step.',
        },
        'global-settings': {
            ...one('open-global-settings'),
            label: 'Settings',
            howTo:
                'Click the settings icon in the top header for contour ' +
                'thickness, dose opacity and layout width.',
            needs: 'It is in the top header.',
        },
        'contour-thickness-setting': {
            ...one('contour-thickness-slider'),
            label: 'Contour thickness',
            opener: 'global-settings',
            howTo: 'Open Settings in the top header and move “Contour thickness”.',
            needs: 'It is in the Settings menu.',
        },
        'dose-opacity-setting': {
            ...one('dose-opacity-slider'),
            label: 'Dose opacity',
            opener: 'global-settings',
            howTo: 'Open Settings in the top header and move “Dose opacity”.',
            needs: 'It is in the Settings menu.',
        },
        'layout-width-setting': {
            ...one('resize-layout-slider'),
            label: 'Layout width',
            opener: 'global-settings',
            howTo: 'Open Settings in the top header and move the layout slider.',
            needs: 'It is in the Settings menu.',
        },
        'add-data': {
            step: 'step-setup',
            ...one('addDataButton'),
            label: 'Add data',
            howTo:
                'On the Setup step click “Add data”, then load files, a folder ' +
                'or from DICOM.',
            needs: 'Open the Setup step.',
        },
        'load-folder': {
            step: 'step-setup',
            ...one('loadFolderButton'),
            label: 'Load folder',
            opener: 'add-data',
            howTo: 'On the Setup step click “Add data”, then “Load folder”.',
            needs: 'Open the Setup step.',
        },
        'review-approve-structure': {
            step: 'step-review',
            ...one('approve-button'),
            label: 'Approve structure (Review)',
            howTo: 'On the Review step, review the structure and click Approve.',
            needs: 'Open the Review step with an unapproved structure set.',
        },
        'review-reject-structure': {
            step: 'step-review',
            ...one('reject-button'),
            label: 'Reject structure (Review)',
            howTo: 'On the Review step, review the structure and click Reject.',
            needs: 'Open the Review step with an unapproved structure set.',
        },
        'review-approve-structure-set': {
            step: 'step-review',
            ...one('approve-set-button'),
            label: 'Approve structure set (Review)',
            howTo:
                'On the Review step, after reviewing, click Approve for the ' +
                'whole structure set.',
            needs: 'Open the Review step and review the structures.',
        },
        'review-reject-structure-set': {
            step: 'step-review',
            ...one('reject-set-button'),
            label: 'Reject structure set (Review)',
            howTo:
                'On the Review step, after reviewing, click Reject for the ' +
                'whole structure set.',
            needs: 'Open the Review step and review the structures.',
        },
        'dose-menu': {
            step: 'step-dose',
            ...one('dose-menu-button'),
            label: 'Dose menu',
            howTo: 'On the Dose step click the dose menu for change and unload.',
            needs: 'Open the Dose step with a dose loaded.',
        },
        'start-auto-segmentation': {
            ...one('start-contour-auto-segmentation-button'),
            label: 'Start auto-segmentation',
            howTo: 'In the Contour+ window choose a model, then click start.',
            needs:
                'Open Contour+ first: “AI Assistant” or “Generate structure ' +
                'set” in the structure set menu.',
        },
    };
